// Data Table widget — the EDITOR front-end for the server's DataTable report spec.
//
// On the canvas this is a single selectable/movable proxy object that PREVIEWS a
// data-bound table (header + a few sample rows + optional totals). It is NOT the
// final table: at render time the server (report.ts) paginates the bound dataset
// to as many rows/pages as needed. This object only authors the spec.
//
// Serialization is the contract: toObject() emits exactly the shape report.ts
// consumes — `{ type:'DataTable', left, top, width (pt), columns, totals, ... }`.
// Geometry is converted px(@96, editor) -> pt(@72, PDF page) so a table placed on
// an A4-sized canvas lands at the same physical spot in the A4 PDF.
import { FabricObject, classRegistry } from 'fabric'
import { lq, rowScope } from './../liquid'

const PT_PER_PX = 72 / 96 // editor px (96dpi) -> PDF points
const PX_PER_PT = 96 / 72

export type CellKind = 'text' | 'image' | 'barcode' | 'qr'
export type ColType = 'text' | 'number' | 'currency' | 'percent' | 'date'
export type Borders = 'grid' | 'horizontal' | 'none'

/** Mirrors server ReportColumn (report.ts) — what the renderer consumes per column. */
export interface DTColumn {
  header: string
  field: string
  width?: number          // pt; undefined = auto
  align?: 'left' | 'center' | 'right'
  cellKind?: CellKind
  barcodeFormat?: string
  cell?: string           // Liquid template, e.g. "{{ amount | money }}"
  type?: ColType
  currency?: string
}
export interface DTTotal { column: string; agg: 'sum' | 'count' | 'avg' }

interface DataTableProps {
  columns?: DTColumn[]
  binding?: { path?: string }
  totals?: DTTotal[]
  totalsLabel?: string
  repeatHeader?: boolean
  rowHeightPt?: number
  borders?: Borders
  headFill?: string
  altRowFill?: string
  font?: string
  sampleRows?: Record<string, any>[]
  /** Typed "summary" table: literal cell templates (one inner array per row),
   *  evaluated server-side with ROOT data scope — supports {{ rows | sum:'x' | money }}.
   *  When set, the table is NOT data-bound (no binding/totals/pagination). */
  staticRows?: string[][]
}

const HEADER_PX = 26
const ROW_PX = 22
const PREVIEW_ROWS = 4

const DEFAULT_COLUMNS: DTColumn[] = [
  { header: 'Description', field: 'description', align: 'left' },
  { header: 'Qty', field: 'qty', align: 'right', type: 'number' },
  { header: 'Price', field: 'price', align: 'right', cell: '{{ price | money }}' },
  { header: 'Amount', field: 'amount', align: 'right', cell: '{{ amount | money }}' },
]
const DEFAULT_SAMPLE: Record<string, any>[] = [
  { description: 'Widget A', qty: 2, price: 9.5, amount: 19 },
  { description: 'Widget B', qty: 1, price: 14.25, amount: 14.25 },
  { description: 'Gadget',   qty: 3, price: 4.0,  amount: 12 },
]

export class DataTable extends FabricObject {
  static type = 'DataTable'

  declare columns: DTColumn[]
  declare binding?: { path?: string }
  declare totals: DTTotal[]
  declare totalsLabel: string
  declare repeatHeader: boolean
  declare rowHeightPt?: number
  declare borders: Borders
  declare headFill: string
  declare altRowFill: string
  declare font?: string
  declare sampleRows: Record<string, any>[]
  declare staticRows?: string[][]

  constructor(options: DataTableProps & Record<string, any> = {}) {
    super({
      originX: 'left', originY: 'top',
      objectCaching: false,
      lockRotation: true,
      strokeWidth: 0, // we draw our own borders in _render; keep stroke out of the bbox
      ...options,
    } as any)
    this.columns = options.columns?.length ? options.columns : DEFAULT_COLUMNS.map((c) => ({ ...c }))
    this.binding = options.binding
    this.totals = options.totals ?? []
    this.totalsLabel = options.totalsLabel ?? 'Total'
    this.repeatHeader = options.repeatHeader ?? true
    this.rowHeightPt = options.rowHeightPt
    this.borders = options.borders ?? 'grid'
    this.headFill = options.headFill ?? '#111827'
    this.altRowFill = options.altRowFill ?? '#f1f5f9'
    this.font = options.font
    this.sampleRows = options.sampleRows?.length ? options.sampleRows : DEFAULT_SAMPLE.map((r) => ({ ...r }))
    this.staticRows = options.staticRows
    if (options.width) this.width = options.width
    if (!this.width) this.width = 360
    this.relayout()
  }

  get isStatic(): boolean { return Array.isArray(this.staticRows) }

  /** Recompute preview height from rows/totals; refresh controls. */
  relayout(): void {
    const n = this.isStatic ? (this.staticRows!.length || 1) : (Math.min(this.sampleRows.length, PREVIEW_ROWS) || 1)
    const totalsRow = this.totals.length && !this.isStatic ? ROW_PX : 0
    this.height = HEADER_PX + n * ROW_PX + totalsRow
    this.dirty = true
    this.setCoords()
    this.canvas?.requestRenderAll()
  }

  /** Per-column pixel widths for the preview (honour explicit pt widths, auto-share the rest). */
  private colWidthsPx(): number[] {
    const total = this.width
    const fixed = this.columns.map((c) => (c.width ? c.width * PX_PER_PT : 0))
    const autoCount = fixed.filter((w) => !w).length
    const used = fixed.reduce((a, b) => a + b, 0)
    const auto = autoCount ? Math.max(20, (total - used) / autoCount) : 0
    return fixed.map((w) => (w ? w : auto))
  }

  private cellText(col: DTColumn, row: Record<string, any>): string {
    if (col.cell) return lq(col.cell, rowScope(row))
    const v = row[col.field] ?? row[col.field?.toLowerCase()]
    return v == null ? '' : String(v)
  }

  _render(ctx: CanvasRenderingContext2D): void {
    const w = this.width, h = this.height
    const x0 = -w / 2, y0 = -h / 2
    const widths = this.colWidthsPx()
    const stat = this.isStatic
    const rows: any[] = stat ? this.staticRows! : this.sampleRows.slice(0, PREVIEW_ROWS)
    const showTotals = this.totals.length > 0 && !stat
    const fontFam = this.font || 'Inter, system-ui, sans-serif'

    // Header band
    ctx.fillStyle = this.headFill
    ctx.fillRect(x0, y0, w, HEADER_PX)

    // Body row backgrounds (alternating)
    let yb = y0 + HEADER_PX
    rows.forEach((_, i) => {
      ctx.fillStyle = i % 2 ? this.altRowFill : '#ffffff'
      ctx.fillRect(x0, yb, w, ROW_PX)
      yb += ROW_PX
    })
    if (showTotals) { ctx.fillStyle = '#e2e8f0'; ctx.fillRect(x0, yb, w, ROW_PX) }

    // Header labels
    ctx.textBaseline = 'middle'
    ctx.font = `700 12px ${fontFam}`
    ctx.fillStyle = '#ffffff'
    let cx = x0
    this.columns.forEach((col, ci) => {
      this.drawCellText(ctx, col.header, cx, y0, widths[ci], HEADER_PX, col.align ?? 'left')
      cx += widths[ci]
    })

    // Body cells
    ctx.font = `400 11px ${fontFam}`
    ctx.fillStyle = '#1f2937'
    rows.forEach((row, ri) => {
      const ry = y0 + HEADER_PX + ri * ROW_PX
      let bx = x0
      this.columns.forEach((col, ci) => {
        const txt = stat ? String(row[ci] ?? '')
          : col.cellKind && col.cellKind !== 'text' ? `[${col.cellKind}]` : this.cellText(col, row)
        this.drawCellText(ctx, txt, bx, ry, widths[ci], ROW_PX, col.align ?? 'left')
        bx += widths[ci]
      })
    })

    // Totals row
    if (showTotals) {
      const ty = y0 + HEADER_PX + rows.length * ROW_PX
      ctx.font = `700 11px ${fontFam}`
      ctx.fillStyle = '#111827'
      let tx = x0
      this.columns.forEach((col, ci) => {
        const t = this.totals.find((x) => x.column === col.field)
        let txt = ci === 0 ? this.totalsLabel : ''
        if (t) {
          const nums = rows.map((r) => Number(r[col.field]) || 0)
          const v = t.agg === 'count' ? rows.length : t.agg === 'avg' ? nums.reduce((a, b) => a + b, 0) / (rows.length || 1) : nums.reduce((a, b) => a + b, 0)
          txt = col.cell ? lq(col.cell, { [col.field]: v }) : String(v)
        }
        this.drawCellText(ctx, txt, tx, ty, widths[ci], ROW_PX, col.align ?? 'left')
        tx += widths[ci]
      })
    }

    // Borders
    if (this.borders !== 'none') {
      ctx.strokeStyle = '#cbd5e1'
      ctx.lineWidth = 1
      const bottom = y0 + h
      // horizontal lines
      ctx.beginPath()
      let yy = y0
      ctx.moveTo(x0, yy); ctx.lineTo(x0 + w, yy)
      yy += HEADER_PX
      const lineCount = rows.length + (showTotals ? 1 : 0)
      for (let i = 0; i <= lineCount; i++) { ctx.moveTo(x0, yy); ctx.lineTo(x0 + w, yy); yy += ROW_PX }
      ctx.stroke()
      // vertical lines (grid only)
      if (this.borders === 'grid') {
        ctx.beginPath()
        let vx = x0
        ctx.moveTo(vx, y0); ctx.lineTo(vx, bottom)
        widths.forEach((cw) => { vx += cw; ctx.moveTo(vx, y0); ctx.lineTo(vx, bottom) })
        ctx.stroke()
      }
    }
    // Outer frame
    ctx.strokeStyle = '#94a3b8'; ctx.lineWidth = 1
    ctx.strokeRect(x0, y0, w, h)
  }

  private drawCellText(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, w: number, h: number, align: 'left' | 'center' | 'right'): void {
    const pad = 5
    let tx = x + pad
    ctx.textAlign = 'left'
    if (align === 'right') { ctx.textAlign = 'right'; tx = x + w - pad }
    else if (align === 'center') { ctx.textAlign = 'center'; tx = x + w / 2 }
    // crude clip so long text doesn't overflow the cell in the preview
    ctx.save()
    ctx.beginPath(); ctx.rect(x, y, w, h); ctx.clip()
    ctx.fillText(text, tx, y + h / 2)
    ctx.restore()
  }

  /** The server-consumable spec for the bound dataset (pt geometry). */
  toSpec(): Record<string, any> {
    const wPt = Math.round((this.width ?? 0) * (this.scaleX ?? 1) * PT_PER_PX)
    const style: any = { borders: this.borders }
    if (this.altRowFill && this.borders === 'horizontal') style.altRowFill = this.altRowFill
    else if (this.altRowFill) style.altRowFill = this.altRowFill
    if (this.headFill) style.head = { fillColor: this.headFill, textColor: 255, fontStyle: 'bold' }
    const stat = this.isStatic
    return {
      type: 'DataTable',
      left: Math.round((this.left ?? 0) * PT_PER_PX),
      top: Math.round((this.top ?? 0) * PT_PER_PX),
      width: wPt,
      columns: this.columns.map((c) => ({ ...c })),
      // static (typed summary) vs data-bound — mutually exclusive on the server.
      staticRows: stat ? this.staticRows : undefined,
      binding: stat ? undefined : this.binding,
      totals: !stat && this.totals.length ? this.totals : undefined,
      totalsLabel: !stat && this.totals.length ? this.totalsLabel : undefined,
      repeatHeader: this.repeatHeader,
      rowHeight: this.rowHeightPt,
      style,
      font: this.font,
    }
  }

  toObject(propertiesToInclude: string[] = []): any {
    const base = super.toObject(propertiesToInclude)
    return {
      ...base,
      ...this.toSpec(),
      // editor-only: preview state, not used by the renderer
      _sampleRows: this.sampleRows,
    }
  }

  static fromObject(object: any): Promise<DataTable> {
    return Promise.resolve(new DataTable({
      ...object,
      left: (object.left ?? 0) * PX_PER_PT,
      top: (object.top ?? 0) * PX_PER_PT,
      width: (object.width ?? 270) * PX_PER_PT,
      scaleX: 1, scaleY: 1,
      columns: object.columns ?? [],
      totals: object.totals ?? [],
      totalsLabel: object.totalsLabel,
      repeatHeader: object.repeatHeader,
      rowHeightPt: object.rowHeight,
      borders: object.style?.borders,
      headFill: object.style?.head?.fillColor,
      altRowFill: object.style?.altRowFill,
      font: object.font,
      sampleRows: object._sampleRows ?? [],
      staticRows: object.staticRows,
    }))
  }
}

classRegistry.setClass(DataTable, DataTable.type)

export function isDataTable(o: unknown): o is DataTable {
  return o instanceof DataTable
}

// ---- Editor surface ------------------------------------------------------
import { getCanvas, state } from './../state'
import { labelPx } from './../canvas'

/** Build sample preview rows + columns from an imported dataset, if any. */
function seedFromBatch(): { columns?: DTColumn[]; sampleRows?: Record<string, any>[] } {
  const headers = state.batchHeaders
  const rows = state.batchData
  if (!headers?.length || !rows?.length) return {}
  const columns: DTColumn[] = headers.map((h) => ({ header: h, field: h, align: 'left' }))
  return { columns, sampleRows: rows.slice(0, PREVIEW_ROWS).map((r) => ({ ...r })) }
}

/** Create a Data Table on the canvas, seeded from the imported dataset when present. */
export function addDataTable(): DataTable {
  const { w, h } = labelPx()
  const seed = seedFromBatch()
  const width = Math.min(Math.max(240, w * 0.9), w - 8)
  const t = new DataTable({ left: Math.max(4, (w - width) / 2), top: Math.max(4, h * 0.15), width, ...seed })
  const canvas = getCanvas()
  canvas.add(t)
  canvas.setActiveObject(t)
  canvas.requestRenderAll()
  return t
}

/** Convert an AI-import data-region placeholder (a flagged rect) into a real DataTable,
 * seeded with the columns the vision model detected, at the same position. */
export function convertDataRegion(rect: FabricObject): DataTable {
  const canvas = getCanvas()
  const meta: any = (rect as any).olmDataRegion ?? {}
  const detected: any[] = Array.isArray(meta.columns) ? meta.columns : []
  const columns: DTColumn[] | undefined = detected.length
    ? detected.map((c, i) => {
        const isMoney = c?.kind === 'currency'
        const field = String(c?.field ?? `col_${i + 1}`)
        return {
          header: String(c?.header ?? field),
          field,
          align: (['left', 'center', 'right'].includes(c?.align) ? c.align : (c?.kind && c.kind !== 'text' ? 'right' : 'left')) as DTColumn['align'],
          type: (['number', 'currency', 'percent', 'date'].includes(c?.kind) ? c.kind : undefined) as DTColumn['type'],
          ...(isMoney ? { cell: `{{ ${field} | money }}` } : {}),
        }
      })
    : undefined

  const left = rect.left ?? 40, top = rect.top ?? 60
  const width = (rect.width ?? 360) * (rect.scaleX ?? 1)
  const regionId = meta.regionId

  // Remove the placeholder rect and its label.
  const dead = canvas.getObjects().filter((o) => o === rect || (o as any).olmDataRegionLabel === regionId)
  dead.forEach((o) => canvas.remove(o))

  const t = new DataTable({ left, top, width: Math.max(120, width), columns })
  canvas.add(t)
  canvas.setActiveObject(t)
  canvas.requestRenderAll()
  return t
}

/** True if an object is an unconverted AI-import data-region placeholder. */
export function isDataRegion(o: unknown): boolean {
  return !!o && typeof o === 'object' && !!(o as any).olmDataRegion
}

export function addColumn(t: DataTable, col: Partial<DTColumn> = {}): DataTable {
  t.columns.push({ header: col.header ?? 'Column', field: col.field ?? 'field', align: col.align ?? 'left', ...col })
  if (t.staticRows) t.staticRows.forEach((r) => r.push('')) // keep static cells aligned to columns
  t.relayout()
  return t
}
export function removeColumn(t: DataTable, index: number): DataTable {
  if (index >= 0 && index < t.columns.length) {
    t.columns.splice(index, 1)
    if (t.staticRows) t.staticRows.forEach((r) => r.splice(index, 1))
    t.relayout()
  }
  return t
}

// ---- Static (typed summary) table ----------------------------------------
export function setStaticMode(t: DataTable, on: boolean): DataTable {
  if (on) {
    if (!t.staticRows) {
      t.columns = [{ header: 'Category', field: 'label', align: 'left' }, { header: 'Amount', field: 'value', align: 'right', width: 120 }]
      t.staticRows = [
        ['Subtotal', '{{ rows | sum: "amount" | money }}'],
        ['Total', '{{ rows | sum: "amount" | money }}'],
      ]
      t.binding = undefined
      t.totals = []
    }
  } else {
    t.staticRows = undefined
  }
  t.relayout()
  return t
}
export function addStaticRow(t: DataTable): DataTable {
  if (t.staticRows) { t.staticRows.push(t.columns.map(() => '')); t.relayout() }
  return t
}
export function removeStaticRow(t: DataTable, i: number): DataTable {
  if (t.staticRows && i >= 0 && i < t.staticRows.length) { t.staticRows.splice(i, 1); t.relayout() }
  return t
}
export function setStaticCell(t: DataTable, r: number, c: number, val: string): DataTable {
  if (t.staticRows?.[r]) { t.staticRows[r][c] = val; t.relayout() }
  return t
}

/** Create a typed summary table (static-rows DataTable) on the canvas. */
export function addSummaryTable(): DataTable {
  const t = addDataTable()
  setStaticMode(t, true)
  getCanvas().requestRenderAll()
  return t
}
export function updateColumn(t: DataTable, index: number, patch: Partial<DTColumn>): DataTable {
  const c = t.columns[index]
  if (c) { Object.assign(c, patch); t.relayout() }
  return t
}
export function setTotals(t: DataTable, totals: DTTotal[]): DataTable { t.totals = totals; t.relayout(); return t }
export function setTableOption(t: DataTable, patch: Partial<Pick<DataTable, 'binding' | 'totalsLabel' | 'repeatHeader' | 'rowHeightPt' | 'borders' | 'headFill' | 'altRowFill' | 'font'>>): DataTable {
  Object.assign(t, patch); t.relayout(); return t
}
